<template>
    <div>
        <b-row>
            <b-col cols="12" lg="8" class="mx-auto">
                <b-card class="modern-card">
                    <h4 class="section-title">{{ $t('connectionSettings') }}</h4>
                    <b-form-group
                        :label="$t('id')"
                        :description="$t('ratchetIdDescription')"
                        label-class="form-label"
                    >
                        <protokol-invalid-feedback :errors="validator.fieldErrors('id')">
                            <b-form-input v-model="connectionId" class="modern-input"></b-form-input>
                        </protokol-invalid-feedback>
                    </b-form-group>
                    <b-form-group
                        :label="$t('type')"
                        label-class="form-label"
                    >
                        <protokol-invalid-feedback :errors="validator.fieldErrors('type')">
                            <b-row>
                                <b-col cols="6" md="3" v-for="dbType in connectionTypes" :key="dbType" class="mb-3">
                                    <div 
                                        class="db-type-box" 
                                        :class="{selected: model.type === dbType}"
                                        @click="model.type = dbType"
                                    >
                                        <img :src="getIcon(dbType)" class="db-icon" />
                                        <div class="db-name">{{ dbType }}</div>
                                    </div>
                                </b-col>
                            </b-row>
                        </protokol-invalid-feedback>
                    </b-form-group>
                    <div class="credentials-section">
                        <h5 class="section-subtitle">{{ $t('credentials') }}</h5>
                        <b-form-group
                            label="Credential Type"
                            label-class="form-label"
                        >
                            <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.type')">
                                <v-select :options="credentialsType" v-model="model.credentials.type" class="modern-select" />
                            </protokol-invalid-feedback>
                        </b-form-group>
                    </div>

                    <template v-if="model.credentials.type == 'DSN'">
                        <b-form-group
                            label="DSN"
                            label-class="form-label"
                            description="Database connection string"
                        >
                        <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.dsn')">
                            <b-form-input v-model="model.credentials.dsn" class="modern-input" />
                        </protokol-invalid-feedback>
                        </b-form-group>
                    </template>

                    <template v-if="model.credentials.type == 'Credentials'">
                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group
                                    label="User"
                                    label-class="form-label"
                                >
                                <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.user')">
                                    <b-form-input v-model="model.credentials.user" class="modern-input" />
                                </protokol-invalid-feedback>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group
                                    label="Password"
                                    label-class="form-label"
                                >
                                <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.password')">
                                    <b-form-input type="password" v-model="model.credentials.password" class="modern-input" />
                                </protokol-invalid-feedback>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group
                                    label="Host"
                                    label-class="form-label"
                                >
                                <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.host')">
                                    <b-form-input v-model="model.credentials.host" class="modern-input" />
                                </protokol-invalid-feedback>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group
                                    label="Database Name"
                                    label-class="form-label"
                                >
                                <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.dbname')">
                                    <b-form-input v-model="model.credentials.dbname" class="modern-input" />
                                </protokol-invalid-feedback>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </template>

                    <template v-if="model.credentials.type == 'Service Account JSON'">
                        <b-form-group
                            label="Service Account JSON"
                            label-class="form-label"
                            description="Paste the entire JSON key file content from Google Cloud Console"
                        >
                        <protokol-invalid-feedback :errors="validator.fieldErrors('credentials.service_account_json')">
                            <b-form-textarea 
                                v-model="model.credentials.service_account_json" 
                                rows="10"
                                class="modern-textarea"
                                placeholder='{"type": "service_account", "project_id": "...", ...}'
                            />
                        </protokol-invalid-feedback>
                        </b-form-group>
                    </template>

                    <b-card-footer class="modern-footer">
                        <b-button variant="success" size="lg" class="modern-btn mr-2" @click="save">
                            <b-icon icon="check-circle" class="mr-2"></b-icon>
                            {{ $t('save') }}
                        </b-button>
                        <b-button variant="outline-warning" size="lg" class="modern-btn" @click="testConnection">
                            <b-icon icon="lightning" class="mr-2"></b-icon>
                            {{ $t('testConnection') }}
                        </b-button>
                    </b-card-footer>
                </b-card>    
            </b-col>

            <b-col cols="12" md="3" v-for="query in model.queries" :key="query.id">
                <b-card>
                    <b-row>
                        <b-col cols="8">{{ query.id }}</b-col>
                        <b-col cols="4"> <b-icon icon="pencil" class="mr-2 action" @click="editAction(query)"/> <b-icon icon="trash" class="action" /></b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import VSelect from 'vue-select'
import CodeEditor from '@/components/form/Editor/Code.vue'
import mysqlIcon from '@/assets/ratchet/mysql.png'
import mongoIcon from '@/assets/ratchet/mongo.png'
import pgsqlIcon from '@/assets/ratchet/pgsql.png'
import bigqueryIcon from '@/assets/ratchet/bigquery.png'

export default {
    name: "CreateRatchet",
    components: {
        VSelect,
        CodeEditor,
    },
    data: function() {
        return {
            model: {
                id: '',
                type: null,
                credentials: {
                    type: null,
                    dsn: null,
                    user: null,
                    password: null,
                    host: null,
                    dbname: null,
                    service_account_json: null
                },
                queries: []
            },


            edit: {
                active: false,
                query: {
                    id: '',
                    expression: null
                }
            },

            create: {
                active: false,
                query: {
                    id: '',
                    expression: null
                }
            },

            connectionTypes: ['MySQL', 'PostgreSQL', 'MongoDB', 'BigQuery'],

            errors: {
                id: null,
                type: null,
                credentials: {
                    type: null,
                    dsn: null,
                    user: null,
                    password: null,
                    host: null,
                    dbname: null,
                    service_account_json: null
                }
            },

            validator: this.$validator()
        }
    },

    watch: {
        model: {
            handler: function() {
                this.validator.revalidate()
            },

            deep: true
        },
    },

    computed: {
        connectionId:{
            get() {
                return this.model.id.replaceAll(" ", "_")
            },

            set(v) {
                this.model.id = v.replaceAll(" ", "_")
            }
        },

        credentialsType() {
            if (this.model.type == 'MongoDB') return ['DSN']
            if (this.model.type == 'BigQuery') return ['Service Account JSON']

            return ['DSN', 'Credentials']
        }
    },

    methods: {
        getIcon(type) {
            const icons = {
                'MySQL': mysqlIcon,
                'PostgreSQL': pgsqlIcon,
                'MongoDB': mongoIcon,
                'BigQuery': bigqueryIcon
            }
            return icons[type]
        },

        testConnection() {
            this.$ratchetApi.post("/test/connection", this.model).then(resp => {
                this.$bvToast.toast(this.$t('connectionEstablished'), {
                    title: this.$t('success'),
                    variant: 'success'
                })
            }).catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            })
        },

        save() {
            if (!this.validator.pass()) return

            let popup = this.$loadingPopup()
            this.$ratchetApi.post("/connection", this.model).then(resp => {
                this.$router.push("/admin/ratchet/edit/" + this.model.id)
            }).catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        },

        validate() {
            const model = this.model

            if (!model.id) this.errors.id = this.$t('fieldRequired')
            if (!model.id) this.errors.type = this.$t('fieldRequired')
        }
    },

    created() {
        this.validator.validate(this.model, {
            idAndType: (model, errors) => {
                if (!model.id) errors.add('id', [this.$t('fieldRequired')])
                if (!model.type) errors.add('type', [this.$t('fieldRequired')])

                return errors
            },

            credentials: (model, errors) => {
                if (!model.credentials.type) errors.add('credentials.type', [this.$t('fieldRequired')])

                if (model.credentials.type == 'Credentials') {
                    if (!model.credentials.user) errors.add('credentials.user', [this.$t('fieldRequired')])   
                    if (!model.credentials.password) errors.add('credentials.password', [this.$t('fieldRequired')])  
                    if (!model.credentials.host) errors.add('credentials.host', [this.$t('fieldRequired')]) 
                    if (!model.credentials.dbname) errors.add('credentials.dbname', [this.$t('fieldRequired')])
                }

                if (model.credentials.type == 'DSN') {
                    if (!model.credentials.dsn) errors.add('credentials.dsn', [this.$t('fieldRequired')])
                }
            }
        })
    }
}
</script>

<style scoped>
.action {
    cursor: pointer;
}

.action:hover {
    color: #ccc;
}

.modern-card {
    background: linear-gradient(145deg, #2a2a2a 0%, #1f1f1f 100%);
    border: 1px solid #3a3a3a;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.section-title {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid #3a3a3a;
}

.section-subtitle {
    color: #aaa;
    font-weight: 500;
    margin-top: 30px;
    margin-bottom: 20px;
}

.credentials-section {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #3a3a3a;
}

.form-label {
    color: #bbb;
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 8px;
}

.modern-input {
    border-radius: 6px;
    padding: 12px 16px;
    transition: all 0.3s ease;
}

.modern-input:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.modern-textarea {
    border-radius: 6px;
    padding: 12px 16px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    transition: all 0.3s ease;
}

.modern-textarea:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.modern-footer {
    background: transparent;
    border-top: 1px solid #3a3a3a;
    padding: 20px 24px;
}

.modern-btn {
    padding: 12px 32px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.modern-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.db-type-box {
    border: 2px solid #444;
    border-radius: 12px;
    padding: 24px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(145deg, #2c2c2c 0%, #242424 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.db-type-box:hover {
    border-color: #666;
    background: linear-gradient(145deg, #333 0%, #2a2a2a 100%);
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.db-type-box.selected {
    border-color: #28a745;
    background: linear-gradient(145deg, #1a4d2e 0%, #164228 100%);
    box-shadow: 0 0 20px rgba(40, 167, 69, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2);
}

.db-icon {
    width: 50px;
    height: 50px;
    object-fit: contain;
    margin-bottom: 12px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.db-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.3px;
}

</style>